import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { useToast } from '@/components/ui/hooks/use-toast';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { comboBoxOptionsOf, selectOption } from '@/components/ui/multi-select-utils';
import { createSessions, getLearningPath, getSessionsForStudentGroup, getStudentGroups } from '@/lib/api-calls';
import { CreateSessionsForGroupsResults, StudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

interface CreateSessionsForStudentGroupsProps {
  studentGroups: StudentGroupWithScheduleAndLevel[];
  trigger: ReactNode;
  shouldSendEmail?: boolean;
  onSuccess?: () => void;
}

const formSchema = z.object({
  overrideStartingLessonId: selectOption,
});

export const CreateSessionsForStudentGroups = ({
  studentGroups,
  trigger,
  shouldSendEmail = false,
  onSuccess,
}: CreateSessionsForStudentGroupsProps) => {
  const studentGroupIds = studentGroups.map((group) => group.id);
  const [results, setResults] = useState<CreateSessionsForGroupsResults>();
  const learningPathId = studentGroups.length > 1 ? undefined : studentGroups.at(0)?.learningPathId;
  const { mutateAsync } = useMutation({
    mutationFn: createSessions,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = ({ formData }: SubmitProps<z.infer<typeof formSchema>>) => {
    const overrideStartingLessonId =
      // need to check for truthy value since default value is ''. See below comment
      !!formData.overrideStartingLessonId?.value && learningPathId
        ? formData.overrideStartingLessonId.value
        : undefined;

    return mutateAsync({
      studentGroupIds,
      overrideStartingLessonId,
      shouldSendEmail,
    })
      .then((results) => {
        setResults(results);
        toast({ title: 'Create sessions completed. See result details in the modal' });
        queryClient.invalidateQueries(getStudentGroups());
        for (const groupId of studentGroupIds) {
          queryClient.invalidateQueries(getSessionsForStudentGroup(groupId));
        }
      })
      .catch(() => {
        toast({ title: 'Error while creating sessions' });
      });
  };

  return (
    <FormModal
      modalTitle="Create Sessions"
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: !!results,
        style: { display: results ? 'none' : undefined },
      }}
      cancelBtnLabel={results ? 'Close' : 'Cancel'}
      onClose={() => {
        if (results && onSuccess) {
          onSuccess();
        }
        setResults(undefined);
      }}
      defaultValues={{
        // workaround for now. Can't make overrideStartingLessonId optional in the form schema
        // since the type checking on FormComboBox doesn't like undefined... will filter out undefined later when submitting
        overrideStartingLessonId: { value: '', label: '' },
      }}
    >
      {({ form }) => {
        return (
          <div className="my-6">
            {results ? (
              <div>
                <div>Successfully created sessions for {results.successGroupIds.length} student group(s)</div>
                <div>Found errors while creating sessions for {results.failedGroupIds.length} student group(s) </div>
                {!!results.errors.length && (
                  <div>
                    <ul>
                      {results.errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  Preparing to generate new sessions for {studentGroupIds.length} student groups. If any group already
                  has scheduled sessions, it will be skipped. Use update future sessions to update sessions for those
                  groups.
                </div>
                {learningPathId && (
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <StartingLessonSelection form={form} learningPathId={learningPathId} />
                  </div>
                )}
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};

const StartingLessonSelection = ({
  form,
  learningPathId,
}: {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  learningPathId: string;
}) => {
  const { data: learningPath, isPending } = useQuery(getLearningPath(learningPathId));
  const allLessonsOptions: MultiSelectOption[] = (learningPath?.learningPathLessons || [])
    .toSorted((l1, l2) => l1.order - l2.order)
    .map((l) => ({
      label: `${l.order}. ${l.lesson.name}`,
      value: l.lessonId,
    }));

  return (
    <FormComboBox
      label="Override starting lesson (default to first lesson)"
      form={form}
      name="overrideStartingLessonId"
      {...comboBoxOptionsOf(allLessonsOptions)}
      triggerProps={{ disabled: isPending }}
    />
  );
};
